import { FormGroup, Button, Input, Alert } from "reactstrap";
import _ from "underscore";

import InputFormatter from "../../Records/Formatters/InputFormatter";
import useToggle from "../../../hooks/useToggle";
import { timeData } from "../../../utils/DynamicValues";
import Constants from "../../../utils/Constants";
import DefaultValueTypeSelector from "./DefaultValueTypeSelector";
import CustomToggle from "./CustomToggle";

const FieldDefault = ({
  fieldValues,
  setFieldValues,
  showModificationProtection,
  disableSubitem,
}) => {
  const [dynamic, toggleDynamic] = useToggle(
    !!fieldValues.defaultValue && fieldValues.defaultValue?.startsWith?.("{{")
  );
  const handleDefaultChange = (e) => {
    const { checked } = e.target;

    setFieldValues((prev) => ({ ...prev, default: checked, defaultValue: "" }));
  };

  // First value is ignored, because InputFormatter usually passes along the field, but we don't need to here as we are directly configuring it.
  const handleChange = (_, val, values) => {
    setFieldValues((prev) => ({
      ...prev,
      defaultValue: val,
      calculatedFieldData: null,
    }));
  };

  const isDisabled = fieldValues.protected && !showModificationProtection;
  const calculatedDefaultFields = Constants.CALCULATED_FIELD_TYPES.filter(
    (v) => v !== "Calculated"
  );
  const defaultValue =
    calculatedDefaultFields?.includes(fieldValues?.fieldType) &&
    !_.isEmpty(fieldValues?.calculatedFieldData)
      ? !!fieldValues?.calculatedFieldData
      : !!fieldValues.default;

  return (
    <>
      <FormGroup>
        <CustomToggle
          title="Default Value"
          description="Choosing Yes allows you to set a default value for this field."
          stateName="default"
          value={defaultValue}
          handleChange={handleDefaultChange}
          disabled={isDisabled}
        />
        {defaultValue && (
          <div
            className="mt-3 d-flex justify-content-start"
            style={{ width: "100%" }}
          >
            {!dynamic &&
            !disableSubitem &&
            calculatedDefaultFields.includes(fieldValues?.fieldType) ? (
              <div style={{ width: "100%" }}>
                <DefaultValueTypeSelector
                  onChange={setFieldValues}
                  fieldValues={fieldValues}
                  component={
                    <>
                      <InputFormatter
                        field={{
                          FieldType: fieldValues.fieldType,
                          SourcesList: fieldValues.source,
                          TrueDisplayName: fieldValues.trueDisplayName,
                          FalseDisplayName: fieldValues.falseDisplayName,
                          ...(Constants.CALCULATED_FIELD_TYPES.includes(
                            fieldValues?.fieldType
                          ) &&
                            fieldValues?.calculatedFieldData && {
                              CalculatedFieldData:
                                fieldValues?.calculatedFieldData,
                            }),
                          Name: fieldValues.name,
                          APIName: fieldValues.apiName,
                        }}
                        value={fieldValues?.defaultValue ?? ""} // ?? because we want to allow for booleans.
                        size="md"
                        onChange={handleChange}
                        disabled={isDisabled}
                        setFieldValues={setFieldValues}
                      />
                    </>
                  }
                />
              </div>
            ) : !dynamic ? (
              <div style={{ width: "100%" }}>
                <InputFormatter
                  field={{
                    FieldType: fieldValues.fieldType,
                    SourcesList: fieldValues.source,
                    TrueDisplayName: fieldValues.trueDisplayName,
                    FalseDisplayName: fieldValues.falseDisplayName,
                    ...(Constants.CALCULATED_FIELD_TYPES.includes(
                      fieldValues?.fieldType
                    ) &&
                      fieldValues?.calculatedFieldData && {
                        CalculatedFieldData: fieldValues?.calculatedFieldData,
                      }),
                    Name: fieldValues.name,
                    APIName: fieldValues.apiName,
                  }}
                  value={fieldValues?.defaultValue ?? ""} // ?? because we want to allow for booleans.
                  size="md"
                  onChange={handleChange}
                  disabled={isDisabled}
                  setFieldValues={setFieldValues}
                />
              </div>
            ) : (
              <Input
                bsSize="sm"
                type="select"
                placeholder="Choose A Dynamic Value"
                style={{ width: "100%" }}
                defaultValue={fieldValues?.defaultValue}
                onChange={(e) => {
                  handleChange(null, e.target.value);
                }}
                value={fieldValues.defaultValue}
              >
                <option value="" disabled>
                  Choose a Dynamic Value
                </option>
                {timeData?.map((val) => (
                  <option key={val?.value} value={val?.value}>
                    {val.label}
                  </option>
                ))}
              </Input>
            )}
            {["DateTime", "Date"].includes(fieldValues.fieldType) && (
              <Button
                size="sm"
                color="secondary"
                className="ml-2 btn-icon-only"
                outline
                onClick={toggleDynamic}
              >
                <i className="mdi mdi-xml"></i>
              </Button>
            )}
          </div>
        )}
      </FormGroup>
      {isDisabled && (
        <Alert color="danger">
          This field is protected. The Default Value cannot be updated.
        </Alert>
      )}
    </>
  );
};

export default FieldDefault;
